import React, { useState } from 'react';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth, db } from '../firebase';
import { updatePassword, reauthenticateWithCredential, EmailAuthProvider, sendEmailVerification, verifyBeforeUpdateEmail } from 'firebase/auth';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import Header from '../components/Header';
import ConfirmationDialog from '../components/ConfirmationDialog';
import { useNavigate } from 'react-router-dom';
import { deleteUserData } from '../utils/userDeletion';
import { formatAuthError } from '../utils/authErrors';
import '../styles.css';

function Account({ subscriptionStatus }) {
  return (
    <>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
      <AccountContent subscriptionStatus={subscriptionStatus} />
    </>
  );
}

function AccountContent({ subscriptionStatus }) {
  const [user] = useAuthState(auth);
  const [syncStatus, setSyncStatus] = useState('offline');
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [error, setError] = useState('');
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [stripeEmail, setStripeEmail] = useState(null);
  const [isLifetime, setIsLifetime] = useState(false);
  const [managingSubscription, setManagingSubscription] = useState(false);
  const navigate = useNavigate();

  const isGoogleUser = user?.providerData?.some(provider => provider.providerId === 'google.com');

  // Fetch Stripe customer email
  React.useEffect(() => {
    if (user?.uid) {
      const fetchStripeEmail = async () => {
        try {
          const customerDoc = await getDoc(doc(db, 'customers', user.uid));
          if (customerDoc.exists()) {
            setStripeEmail(customerDoc.data().email);
          }
        } catch (error) {
          console.error('Error fetching Stripe email:', error);
        }
      };
      fetchStripeEmail();
    }
  }, [user?.uid]);

  // Fetch lifetime status
  React.useEffect(() => {
    if (user?.uid) {
      const fetchLifetimeStatus = async () => {
        try {
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists()) {
            setIsLifetime(userDoc.data().lifetime || false);
          }
        } catch (error) {
          console.error('Error fetching lifetime status:', error);
        }
      };
      fetchLifetimeStatus();
    }
  }, [user?.uid]);

  const sendVerificationEmail = async () => {
    try {
      await sendEmailVerification(user);
      alert('Verification email sent. Please check your email and click the link to verify your account.');
    } catch (err) {
      setError(formatAuthError(err));
    }
  };

  React.useEffect(() => {
    setSyncStatus(navigator.onLine ? 'synced' : 'offline');
  }, []);

  const getCurrentPlanName = () => {
    if (!subscriptionStatus) return 'Loading...';
    if (isLifetime) return 'Pro Lifetime';
    if (subscriptionStatus.status === 'trial') {
      return (subscriptionStatus.plan === 'pro' ? 'Pro Trial' : 'Enthusiast Trial');
    }
    if (subscriptionStatus.status === 'active') {
      return (subscriptionStatus.plan === 'pro' ? 'Pro' : 'Enthusiast');
    }
    if (subscriptionStatus.status === 'expired') return 'Trial Expired';
    if (subscriptionStatus.status === 'canceled' || subscriptionStatus.status === 'past_due') return 'Account On Hold';
    return 'Free'; // Default for users without subscriptions
  };

  const handleManageSubscription = async () => {
    setManagingSubscription(true);
    try {
      const createPortalLink = httpsCallable(functions, 'ext-firestore-stripe-payments-createPortalLink');
      const result = await createPortalLink({ returnUrl: `${window.location.origin}/account` });
      window.location.assign(result.data.url);
    } catch (err) {
      console.error('Error opening subscription portal:', err);
      alert('Unable to open subscription management. Please try again later.');
      setManagingSubscription(false);
    }
  };

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword) {
      setError('Please fill in all fields');
      return;
    }
    if (newPassword === currentPassword) {
      setError('The new password is the same as the current password.');
      return;
    }
    try {
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, credential);
      await updatePassword(user, newPassword);
      setShowPasswordDialog(false);
      setCurrentPassword('');
      setNewPassword('');
      setError('');
      alert('Password updated successfully');
    } catch (err) {
      setError(formatAuthError(err));
    }
  };

  const handleChangeEmail = async () => {
    if (!currentPassword || !newEmail) {
      setError('Please fill in all fields');
      return;
    }
    const normalizedNewEmail = newEmail.toLowerCase().trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedNewEmail)) {
      setError('Please enter a valid email address.');
      return;
    }
    if (normalizedNewEmail === user.email) {
      setError('The new email is the same as the current email.');
      return;
    }
    if (!user.emailVerified) {
      setError('Please verify your email address before changing it.');
      return;
    }
    try {
      console.log('Starting email change process for:', normalizedNewEmail);
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, credential);
      console.log('Reauthenticated successfully');
      await verifyBeforeUpdateEmail(user, normalizedNewEmail);
      console.log('Verification email sent successfully');
      setShowEmailDialog(false);
      setCurrentPassword('');
      setNewEmail('');
      setError('');
      alert('Verification email sent to your new email address. Please click the link in the email to complete the email change.');
    } catch (err) {
      console.error('Error in email change process:', err);
      setError(formatAuthError(err));
    }
  };

  const handleDeleteAccount = async () => {
    if (!currentPassword) {
      setError('Please enter your password to confirm deletion');
      return;
    }

    if (deleteConfirmation !== 'DELETE_MY_ACCOUNT') {
      setError('Please type "DELETE_MY_ACCOUNT" to confirm deletion');
      return;
    }

    setDeletingAccount(true);
    setError('');

    try {
      // Reauthenticate user before deletion
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, credential);

      // Delete all user data
      await deleteUserData(user, deleteConfirmation);

      // Sign out (though deleteUser should automatically sign out)
      await auth.signOut();

      // Redirect to home page
      navigate('/');
      alert('Your account has been permanently deleted');

    } catch (err) {
      setError(formatAuthError(err));
    } finally {
      setDeletingAccount(false);
    }
  };

  return (
    <div>
      <Header syncStatus={syncStatus} />
      <div className="settings-container">
        <div className="page-title">
          <h1>My Account</h1>
          <button onClick={() => navigate('/')} className="close-button">✕</button>
        </div>
        <div className="settings-card">
          <h3>User Information</h3>
          <p>Email: {user?.email || 'Loading...'}</p>
          <p>User ID: {user?.uid || 'Loading...'}</p>
          {!isGoogleUser && (
            <p>
              Email Verified: {user?.emailVerified ? 'Yes' : 'No'}
              {!user?.emailVerified && (
                <button onClick={sendVerificationEmail} className="btn btn-save" style={{ marginLeft: '10px' }}>
                  Send Verification Email
                </button>
              )}
            </p>
          )}
          {stripeEmail && user?.email && stripeEmail !== user.email && (
            <div style={{ marginTop: '15px', padding: '10px', backgroundColor: '#fff3cd', border: '1px solid #ffeaa7', borderRadius: '4px' }}>
              <p style={{ margin: '0', fontSize: '14px' }}>
                <strong>Billing Email Notice:</strong> Your billing email in Stripe ({stripeEmail}) doesn't match your current account email ({user.email}).
                If you would also like your billing email address updated, please contact us at hello@hotesetup.app and we would be happy to manually update that for you.
              </p>
            </div>
          )}
        </div>
        <div className="settings-card">
          <h3>Subscription Type</h3>
          <p>Current Plan: {getCurrentPlanName()}</p>
          {subscriptionStatus?.status === 'active' && !isLifetime && (
            <button onClick={handleManageSubscription} className="btn btn-save" disabled={managingSubscription}>
              {managingSubscription ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                  <div style={{
                    width: '16px',
                    height: '16px',
                    border: '2px solid rgba(255,255,255,0.3)',
                    borderTop: '2px solid white',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }}></div>
                  Processing...
                </div>
              ) : (
                'Manage Subscription'
              )}
            </button>
          )}
        </div>
        {isGoogleUser ? (
          <div className="settings-card">
            <h3>Sign In Method</h3>
            <p>This account uses Google for sign-in. If you'd like to change your email address, please contact us by email to migrate your account.</p>
          </div>
        ) : (
          <>
            <div className="settings-card">
              <h3>Change Password</h3>
              <button onClick={() => setShowPasswordDialog(true)} className="btn btn-save">Change Password</button>
            </div>
            <div className="settings-card">
              <h3>Change Email Address</h3>
              <button onClick={() => setShowEmailDialog(true)} className="btn btn-save">Change Email</button>
            </div>
          </>
        )}
        <div className="settings-card danger">
          <h3>Delete Account</h3>
          <p className="danger-text">
            This action cannot be undone. All your data, including vehicles, sessions, and backups will be permanently deleted.
          </p>
          <button onClick={() => setShowDeleteDialog(true)} className="btn delete-button">
            Delete Account
          </button>
        </div>
      </div>
      {showPasswordDialog && (
        <ConfirmationDialog
          message={
            <div>
              <h2>Change Password</h2>
              <input
                type="password"
                placeholder="Current Password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                style={{ width: 'calc(100% - 20px)', padding: '8px', border: '1px solid #ccc', borderRadius: '4px', marginBottom: '10px', marginTop: '20px' }}
              />
              <input
                type="password"
                placeholder="New Password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                style={{ width: 'calc(100% - 20px)', padding: '8px', border: '1px solid #ccc', borderRadius: '4px', marginBottom: '10px' }}
              />
              {error && <p style={{ color: 'red' }}>{error}</p>}
            </div>
          }
          onConfirm={handleChangePassword}
          onCancel={() => { setShowPasswordDialog(false); setCurrentPassword(''); setNewPassword(''); setError(''); }}
          confirmText="Update Password"
        />
      )}
      {showEmailDialog && (
        <ConfirmationDialog
          message={
            <div>
              <h2>Change Email</h2>
              <p style={{ fontSize: '14px', marginBottom: '15px', color: '#666' }}>
                A verification email will be sent to your new email address. Click the link in the email to complete the change.
              </p>
              <input
                type="password"
                placeholder="Current Password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                style={{ width: 'calc(100% - 20px)', padding: '8px', border: '1px solid #ccc', borderRadius: '4px', marginBottom: '10px', marginTop: '10px' }}
              />
              <input
                type="email"
                placeholder="New Email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                style={{ width: 'calc(100% - 20px)', padding: '8px', border: '1px solid #ccc', borderRadius: '4px', marginBottom: '10px' }}
              />
              {error && <p style={{ color: 'red' }}>{error}</p>}
            </div>
          }
          onConfirm={handleChangeEmail}
          onCancel={() => { setShowEmailDialog(false); setCurrentPassword(''); setNewEmail(''); setError(''); }}
          confirmText="Send Verification Email"
        />
      )}
      {showDeleteDialog && (
        <ConfirmationDialog
          message={
            <div>
              <h3 style={{ color: '#dc3545' }}>Delete Account Permanently</h3>
              <p>This will permanently delete your account and all associated data including:</p>
              <ul style={{ textAlign: 'left', margin: '10px 0' }}>
                <li>All vehicle setups</li>
                <li>All session data</li>
                <li>All circuit information</li>
                <li>All backups</li>
                <li>Your subscription information</li>
              </ul>
              <p style={{ color: '#dc3545', fontWeight: 'bold' }}>This action cannot be undone!</p>
              <div style={{ marginTop: '15px' }}>
                <label htmlFor="deletePassword" style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Enter your password to confirm:
                </label>
                <input
                  type="password"
                  id="deletePassword"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
                  placeholder="Your password"
                />
              </div>
              <div style={{ marginTop: '15px' }}>
                <label htmlFor="deleteConfirmation" style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Type "DELETE_MY_ACCOUNT" to confirm:
                </label>
                <input
                  type="text"
                  id="deleteConfirmation"
                  value={deleteConfirmation}
                  onChange={(e) => setDeleteConfirmation(e.target.value)}
                  style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
                  placeholder="DELETE_MY_ACCOUNT"
                />
              </div>
              {error && <p style={{ color: '#dc3545', marginTop: '10px' }}>{error}</p>}
            </div>
          }
          onConfirm={handleDeleteAccount}
          onCancel={() => { setShowDeleteDialog(false); setCurrentPassword(''); setDeleteConfirmation(''); setError(''); }}
          confirmText="Yes, Delete My Account"
          confirmClassName="delete-button"
          loading={deletingAccount}
        />
      )}
    </div>
  );
}

export default Account;